import moment from 'moment'

export const checkIfAcknowledged = (item) => {
    let acknowledged = false

    if (item && item.acknowledgments) {
        item.acknowledgments.forEach(ack => {
            let days = moment().diff(moment(ack.timestamp), 'days')
            if (days > 365 || !ack.active) {
                acknowledged = false
            } else {
                acknowledged = ack
            }
        })
    }

    return acknowledged
}

// Memoization cache for getLatestAcknowledgements
const ackCache = new Map();
// Maximum size for the cache to prevent memory leaks
const MAX_CACHE_SIZE = 1000;

/**
 * Get the latest acknowledgement from a data structure
 * Optimized with caching to reduce processing time on repeated calls
 * @param {Object} data - The data object containing acknowledgements
 * @param {string} tenantName - Optional tenant name to validate acknowledgement belongs to correct tenant
 * @returns {Object|null} The latest acknowledgement or null if none found
 */
export const getLatestAcknowledgements = (data, tenantName = null) => {
    // Check if data is null/undefined or doesn't have acknowledgements property
    if (!data || !data.acknowledgements || !Array.isArray(data.acknowledgements) || data.acknowledgements.length === 0) {
        return null;
    }

    // Log for debugging - remove this in production
    if (tenantName && data.tenant && data.tenant !== tenantName) {
        console.warn(`Acknowledgement data mismatch: expected tenant "${tenantName}" but got "${data.tenant}"`);
    }

    try {
        const latestTimestamp = Math.max(...data.acknowledgements.map(ack =>
            ack.timestamp ? new Date(ack.timestamp).getTime() : 0
        ));

        const sampleAck = data.acknowledgements[0];
        const tenantKey = tenantName || data.tenant || '';
        // Include d2 presence in the cache key so stale entries (without d2)
        // are invalidated once richer data arrives from a different query.
        const d2Fingerprint = data.acknowledgements
            .map(a => a?.acknowledgedBy?.d2 || '_')
            .join('');
        const cacheKey = `${sampleAck?.pk || ''}_${sampleAck?.sk || ''}_${tenantKey}_${latestTimestamp}_${d2Fingerprint}`;

        // Check if we have a cached result
        if (ackCache.has(cacheKey)) {
            return ackCache.get(cacheKey);
        }

        // Use reduce to find the most recent acknowledgement
        // When timestamps are equal, prefer the record that appears later in the array (likely created last)
        // Also add additional validation for data integrity
        const latestAck = data.acknowledgements.reduce((prev, current) => {
            // Basic validation
            if (!current.timestamp) return prev;
            if (!prev.timestamp) return current;

            // Primary sort: by timestamp (descending)
            if (current.timestamp > prev.timestamp) return current;
            if (current.timestamp < prev.timestamp) return prev;

            // When timestamps are equal, use array position as tiebreaker
            const currentIndex = data.acknowledgements.indexOf(current);
            const prevIndex = data.acknowledgements.indexOf(prev);
            return currentIndex > prevIndex ? current : prev;
        });

        // Additional validation: ensure the acknowledgement has required fields
        if (latestAck && (!latestAck.acknowledgedBy || !latestAck.timestamp)) {
            console.warn('Latest acknowledgement missing required fields:', latestAck);
        }

        // Store in cache
        ackCache.set(cacheKey, latestAck);

        // Prevent cache from growing too large
        if (ackCache.size > MAX_CACHE_SIZE) {
            // Remove oldest entry (first key in Map)
            const firstKey = ackCache.keys().next().value;
            ackCache.delete(firstKey);
        }

        return latestAck;
    } catch (error) {
        console.error('Error in getLatestAcknowledgements:', error);
        // Fallback to non-cached version if there's any error in caching logic
        return data.acknowledgements.reduce((prev, current) => {
            if (!current.timestamp) return prev;
            if (!prev.timestamp) return current;

            if (current.timestamp > prev.timestamp) return current;
            if (current.timestamp < prev.timestamp) return prev;

            const currentIndex = data.acknowledgements.indexOf(current);
            const prevIndex = data.acknowledgements.indexOf(prev);
            return currentIndex > prevIndex ? current : prev;
        });
    }
}